import { BarChart } from '@mantine/charts';
import { Box, Grid, Group, SimpleGrid, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconBuilding, IconCalendarDue, IconCash, IconTrendingUp } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { Dashboard, DashboardOrg } from '../api/types';
import { BarsCard, dayRange, TrendCard } from '../components/charts';
import { HealthDot } from '../components/subscription';
import { tr } from '../lib/i18n';
import { fmtAgo, fmtDay, fmtDaysLeft, fmtMoney, fmtNum, HEALTH, PAYMENT_METHOD, SUBSCRIPTION_STATUS } from '../lib/labels';
import { EmptyState, ErrorState, Kpi, Loading, Panel, StatusBadge, toneOf } from '../ui/kit';

const MONTHS = [
  tr('كانون الثاني', 'Jan'), tr('شباط', 'Feb'), tr('آذار', 'Mar'), tr('نيسان', 'Apr'), tr('أيار', 'May'), tr('حزيران', 'Jun'),
  tr('تموز', 'Jul'), tr('آب', 'Aug'), tr('أيلول', 'Sep'), tr('تشرين الأول', 'Oct'), tr('تشرين الثاني', 'Nov'), tr('كانون الأول', 'Dec'),
];
const monthLabel = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

function last12Months(today: string): string[] {
  const out: string[] = [];
  const d = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 11);
  for (let i = 0; i < 12; i++) {
    out.push(d.toISOString().slice(0, 7));
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return out;
}

const SUB_COLOR: Record<string, string> = { ACTIVE: 'teal.6', EXPIRING: 'orange.5', EXPIRED: 'red.6', NONE: 'gray.4' };
const v = (c: string) => `var(--mantine-color-${c.replace('.', '-')})`;

/** One bar split by subscription state; each part opens the filtered list. */
function SubscriptionBar({ d, onPick }: { d: Dashboard; onPick: (k: string) => void }) {
  const parts = (['ACTIVE', 'EXPIRING', 'EXPIRED', 'NONE'] as const).map((k) => ({
    k,
    n: { ACTIVE: d.subscriptions.active, EXPIRING: d.subscriptions.expiring, EXPIRED: d.subscriptions.expired, NONE: d.subscriptions.none }[k],
    label: SUBSCRIPTION_STATUS[k]!.label,
  }));
  const total = parts.reduce((s, p) => s + p.n, 0) || 1;
  return (
    <Panel title={tr('الاشتراكات', 'Subscriptions')} description={tr('اضغط أي حالة لفتح قائمة شركاتها', 'Click a state to open its companies')}>
      <Group gap={3} h={14} wrap="nowrap" mb="md" style={{ borderRadius: 99, overflow: 'hidden' }}>
        {parts.filter((p) => p.n > 0).map((p) => (
          <Tooltip key={p.k} label={`${p.label}: ${fmtNum(p.n)}`}>
            <UnstyledButton onClick={() => onPick(p.k)} h="100%" style={{ flex: p.n, background: v(SUB_COLOR[p.k]!), minWidth: 6 }} aria-label={p.label} />
          </Tooltip>
        ))}
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
        {parts.map((p) => (
          <UnstyledButton key={p.k} onClick={() => onPick(p.k)} p="sm" style={{ borderRadius: 10, border: '1px solid var(--pf-line)' }} className="pf-tile">
            <Group gap={6} wrap="nowrap">
              <Box w={8} h={8} style={{ borderRadius: 99, background: v(SUB_COLOR[p.k]!), flexShrink: 0 }} />
              <Text size="xs" c="dimmed" truncate>{p.label}</Text>
            </Group>
            <Group gap={6} align="baseline" mt={4}>
              <Text fz={22} fw={600} className="pf-num">{fmtNum(p.n)}</Text>
              <Text size="xs" c="dimmed">{Math.round((p.n / total) * 100)}%</Text>
            </Group>
            {p.k === 'EXPIRING' && <Text size="xs" c="dimmed">{tr('خلال 7 أيام', 'Within 7 days')}</Text>}
            {p.k === 'EXPIRED' && d.subscriptions.locked > 0 && <Text size="xs" c="red.7">{tr(`${fmtNum(d.subscriptions.locked)} قراءة فقط`, `${fmtNum(d.subscriptions.locked)} read-only`)}</Text>}
          </UnstyledButton>
        ))}
      </SimpleGrid>
    </Panel>
  );
}

function OrgList({ title, description, rows, empty, right }: { title: string; description?: string; rows: DashboardOrg[]; empty: string; right: (o: DashboardOrg) => ReactNode }) {
  const nav = useNavigate();
  return (
    <Panel title={title} description={description} flush h="100%">
      {rows.length === 0 ? (
        <EmptyState title={empty} />
      ) : (
        <Stack gap={0}>
          {rows.map((o, i) => (
            <UnstyledButton key={o.id} onClick={() => nav(`/organizations/${o.id}`)} px="lg" py={10} className="pf-row" style={{ borderTop: i ? '1px solid var(--pf-line)' : undefined }}>
              <Group gap="sm" wrap="nowrap">
                <HealthDot health={o.health} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>{o.name}</Text>
                  <Text size="xs" c="dimmed" truncate>{o.planName ?? tr('بدون باقة', 'No plan')}</Text>
                </Box>
                <Box ta="end" style={{ flexShrink: 0 }}>{right(o)}</Box>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
    </Panel>
  );
}

/** Platform home: money, subscriptions and the pulse of every company — what to do today. */
export function DashboardPage() {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => api<Dashboard>('/dashboard'), refetchInterval: 60_000 });
  if (q.isLoading) return <Loading h={400} />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;

  const months = last12Months(d.today);
  const revenueByMonth = new Map(d.revenue.byMonth.map((r) => [r.month, Number(r.total)]));
  const growthByMonth = new Map(d.growth.map((r) => [r.month, r.count]));
  const newIn12 = months.map((m) => growthByMonth.get(m) ?? 0).reduce((a, b) => a + b, 0);
  const days = dayRange(d.activity[0]?.day ?? d.today, d.today);
  const act = (entity: 'PROPERTY' | 'REQUEST') => {
    const m = new Map(d.activity.filter((a) => a.entity === entity).map((a) => [a.day, a.total]));
    return days.map((x) => m.get(x) ?? 0);
  };
  const icon = { size: 16, stroke: 1.7 };

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 2, lg: 4 }} spacing={{ base: 'sm', sm: 'lg' }}>
        <Kpi label={tr('الدخل هذا الشهر', 'Revenue this month')} value={fmtMoney(d.revenue.month)} hint={tr(`${fmtNum(d.revenue.month_count)} دفعة`, `${fmtNum(d.revenue.month_count)} payment(s)`)} icon={<IconCash {...icon} />} />
        <Kpi label={tr('الدخل هذه السنة', 'Revenue this year')} value={fmtMoney(d.revenue.year)} hint={tr(`منذ البداية: ${fmtMoney(d.revenue.all_time)}`, `All time: ${fmtMoney(d.revenue.all_time)}`)} icon={<IconTrendingUp {...icon} />} />
        <Kpi
          label={tr('تجديدات متوقعة خلال 30 يوماً', 'Expected renewals, 30 days')}
          value={fmtMoney(d.subscriptions.expectedRenewals30d.amount)}
          hint={tr(`${fmtNum(d.subscriptions.expectedRenewals30d.count)} شركة — حسب سعر باقتها`, `${fmtNum(d.subscriptions.expectedRenewals30d.count)} companies — at plan price`)}
          icon={<IconCalendarDue {...icon} />}
        />
        <UnstyledButton onClick={() => nav('/organizations')} style={{ borderRadius: 12 }}>
          <Kpi
            label={tr('الشركات النشطة', 'Active companies')}
            value={fmtNum(d.organizations.active)}
            hint={tr(`${fmtNum(d.organizations.newThisMonth)} جديدة هذا الشهر · ${fmtNum(d.organizations.suspended)} معلّقة`, `${fmtNum(d.organizations.newThisMonth)} new this month · ${fmtNum(d.organizations.suspended)} suspended`)}
            icon={<IconBuilding {...icon} />}
          />
        </UnstyledButton>
      </SimpleGrid>

      <Grid gap="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <SubscriptionBar d={d} onPick={(k) => nav(`/organizations?subscription=${k}`)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Panel title={tr('نبض الشركات', 'Company pulse')} description={tr('حسب الدخول والحركة', 'By sign-ins and activity')} h="100%">
            <SimpleGrid cols={2} spacing="xs">
              {(['ACTIVE', 'COOLING', 'SLEEPING', 'NEW'] as const).map((h) => (
                <Tooltip key={h} label={HEALTH[h]!.hint} multiline maw={240}>
                  <Box p="sm" style={{ borderRadius: 10, border: '1px solid var(--pf-line)' }}>
                    <HealthDot health={h} withLabel />
                    <Text fz={22} fw={600} className="pf-num" mt={2}>{fmtNum(d.health[h.toLowerCase() as keyof Dashboard['health']])}</Text>
                  </Box>
                </Tooltip>
              ))}
            </SimpleGrid>
          </Panel>
        </Grid.Col>
      </Grid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <OrgList
          title={tr('اتصل بهم اليوم', 'Call them today')}
          description={tr('اشتراكات تنتهي خلال 14 يوماً أو انتهت مؤخراً', 'Ending within 14 days or recently expired')}
          rows={d.lists.renewals}
          empty={tr('لا أحد — كل الاشتراكات بعيدة عن الانتهاء', 'No one — every subscription is far from ending')}
          right={(o) => (
            <Stack gap={2} align="flex-end">
              <StatusBadge tone={o.locked ? 'red' : toneOf(SUBSCRIPTION_STATUS[o.subscriptionStatus]!.color)}>{fmtDaysLeft(o.daysLeft)}</StatusBadge>
              {o.planPrice && <Text size="xs" c="dimmed" className="pf-num">{fmtMoney(o.planPrice)}</Text>}
            </Stack>
          )}
        />
        <OrgList
          title={tr('شركات نائمة', 'Sleeping companies')}
          description={tr('لا دخول منذ أسبوعين ولا حركة خلال 30 يوماً', 'No login for two weeks, no activity in 30 days')}
          rows={d.lists.sleeping}
          empty={tr('لا توجد شركات نائمة', 'No sleeping companies')}
          right={(o) => <Text size="xs" c="dimmed">{fmtAgo(o.lastLoginAt)}</Text>}
        />
      </SimpleGrid>

      <Grid gap="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Panel
            title={tr('الدخل — آخر 12 شهراً', 'Revenue — last 12 months')}
            description={tr(`شركات جديدة: ${newIn12} خلال 12 شهراً · هذا الشهر ${fmtNum(d.organizations.newThisMonth)}`, `New companies: ${newIn12} in 12 months · ${fmtNum(d.organizations.newThisMonth)} this month`)}
            h="100%"
          >
            <BarChart
              h={260}
              data={months.map((m) => ({ month: monthLabel(m), revenue: revenueByMonth.get(m) ?? 0 }))}
              dataKey="month"
              series={[{ name: 'revenue', label: tr('الدخل', 'Revenue'), color: 'ink.9' }]}
              gridAxis="y"
              tickLine="none"
              barProps={{ radius: [4, 4, 0, 0] }}
              maxBarWidth={28}
              valueFormatter={(x) => fmtMoney(x)}
              yAxisProps={{ width: 70, tickFormatter: (x: number) => fmtNum(x) }}
            />
          </Panel>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <BarsCard title={tr('الشركات حسب الباقة', 'Companies by plan')} rows={d.subscriptions.byPlan.map((p) => ({ label: p.name, value: p.count }))} />
        </Grid.Col>
      </Grid>

      <Grid gap="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <TrendCard title={tr('حركة كل الشركات — آخر 30 يوماً', 'Activity across all companies — last 30 days')} days={days} series={[{ label: tr('الطلبات', 'Requests'), data: act('REQUEST') }, { label: tr('العقارات', 'Properties'), data: act('PROPERTY') }]} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="lg" h="100%">
            <OrgList title={tr('الأكثر نشاطاً', 'Most active')} description={tr('عقارات + طلبات خلال 30 يوماً', 'Properties + requests in 30 days')} rows={d.lists.topActive} empty={tr('لا حركة بعد', 'No activity yet')}
              right={(o) => <Text fw={600} className="pf-num">{fmtNum(o.activity30d)}</Text>} />
            <Panel title={tr('آخر الدفعات', 'Recent payments')} flush>
              {d.lists.recentPayments.length === 0 ? (
                <EmptyState title={tr('لا دفعات بعد', 'No payments yet')} text={tr('سجّلها من صفحة الشركة ← «تجديد»', 'Record them from the company page → “Renew”')} />
              ) : (
                <Stack gap={0}>
                  {d.lists.recentPayments.map((p, i) => (
                    <UnstyledButton key={p.id} onClick={() => nav(`/organizations/${p.organization_id}`)} px="lg" py={10} className="pf-row" style={{ borderTop: i ? '1px solid var(--pf-line)' : undefined }}>
                      <Group gap="sm" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate>{p.organization_name}</Text>
                          <Text size="xs" c="dimmed" truncate>{p.plan_name} · {PAYMENT_METHOD[p.method] ?? p.method} · {fmtDay(p.created_at)}</Text>
                        </Box>
                        <Text size="sm" fw={600} className="pf-num" style={{ flexShrink: 0 }}>{fmtMoney(p.amount, p.currency)}</Text>
                      </Group>
                    </UnstyledButton>
                  ))}
                </Stack>
              )}
            </Panel>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
